import React, { useState, useEffect } from 'react'
import { 
  ChartBarIcon, 
  TruckIcon,
  ClockIcon,
  BoltIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  ChartPieIcon
} from '@heroicons/react/24/outline'

interface RendimientoVehiculo {
  id: string
  placa: string
  modelo: string
  kmRecorridos: number
  horasOperacion: number
  consumoCombustible: number
  eficienciaCombustible: number
  viajesCompletados: number
  tiempoPromedioPorViaje: number
  ingresos: number
}

interface MetricaGeneral {
  label: string
  valor: string
  cambio: number
  icon: React.ComponentType<any>
  color: string
}

const PaginaReporteRendimiento: React.FC = () => {
  const [vehiculos, setVehiculos] = useState<RendimientoVehiculo[]>([])
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState<string>('todos')

  useEffect(() => {
    // Datos de ejemplo
    const vehiculosEjemplo: RendimientoVehiculo[] = [
      {
        id: '1',
        placa: 'ABC-123',
        modelo: 'Mercedes-Benz Actros',
        kmRecorridos: 12500,
        horasOperacion: 280,
        consumoCombustible: 3200,
        eficienciaCombustible: 3.9,
        viajesCompletados: 45,
        tiempoPromedioPorViaje: 6.2,
        ingresos: 18500000
      },
      {
        id: '2',
        placa: 'DEF-456',
        modelo: 'Volvo FH',
        kmRecorridos: 11800,
        horasOperacion: 265,
        consumoCombustible: 3100,
        eficienciaCombustible: 3.8,
        viajesCompletados: 42,
        tiempoPromedioPorViaje: 6.3,
        ingresos: 17200000
      },
      {
        id: '3',
        placa: 'GHI-789',
        modelo: 'Scania R-Series',
        kmRecorridos: 13200,
        horasOperacion: 295,
        consumoCombustible: 3400,
        eficienciaCombustible: 3.9,
        viajesCompletados: 48,
        tiempoPromedioPorViaje: 6.1,
        ingresos: 19800000
      }
    ]
    setVehiculos(vehiculosEjemplo)

    // Establecer fechas por defecto (último mes)
    const hoy = new Date()
    const hace30Dias = new Date(hoy.getTime() - 30 * 24 * 60 * 60 * 1000)
    setFechaFin(hoy.toISOString().split('T')[0])
    setFechaInicio(hace30Dias.toISOString().split('T')[0])
  }, [])

  const vehiculosFiltrados = vehiculoSeleccionado === 'todos' 
    ? vehiculos 
    : vehiculos.filter(v => v.id === vehiculoSeleccionado)

  const calcularMetricasGenerales = (): MetricaGeneral[] => {
    const totalKm = vehiculosFiltrados.reduce((sum, v) => sum + v.kmRecorridos, 0)
    const totalViajes = vehiculosFiltrados.reduce((sum, v) => sum + v.viajesCompletados, 0)
    const totalIngresos = vehiculosFiltrados.reduce((sum, v) => sum + v.ingresos, 0)
    const promedioEficiencia = vehiculosFiltrados.reduce((sum, v) => sum + v.eficienciaCombustible, 0) / vehiculosFiltrados.length

    return [
      {
        label: 'Kilómetros Totales',
        valor: `${totalKm.toLocaleString()} km`,
        cambio: 8.2,
        icon: TruckIcon,
        color: 'blue'
      },
      {
        label: 'Viajes Completados',
        valor: totalViajes.toString(),
        cambio: 12.5,
        icon: ChartBarIcon,
        color: 'green'
      },
      {
        label: 'Ingresos Totales',
        valor: `$${(totalIngresos / 1000000).toFixed(1)}M`,
        cambio: 15.3,
        icon: ChartPieIcon,
        color: 'purple'
      },
      {
        label: 'Eficiencia Promedio',
        valor: `${promedioEficiencia.toFixed(1)} km/L`,
        cambio: -2.1,
        icon: BoltIcon,
        color: 'yellow'
      }
    ]
  }

  const metricasGenerales = calcularMetricasGenerales()

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
      green: 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
      purple: 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400',
      yellow: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400'
    }
    return colors[color as keyof typeof colors] || colors.blue
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reporte de Rendimiento</h1>
            <p className="text-gray-600 dark:text-gray-400">Análisis del rendimiento operacional de la flota</p>
          </div>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <ArrowDownTrayIcon className="h-5 w-5" />
            <span>Exportar Reporte</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha Inicio
            </label>
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha Fin
            </label>
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Vehículo
            </label>
            <select
              value={vehiculoSeleccionado}
              onChange={(e) => setVehiculoSeleccionado(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="todos">Todos los vehículos</option>
              {vehiculos.map((vehiculo) => (
                <option key={vehiculo.id} value={vehiculo.id}>
                  {vehiculo.placa} - {vehiculo.modelo}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
              Generar Reporte
            </button>
          </div>
        </div>
      </div>

      {/* Métricas generales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        {metricasGenerales.map((metrica, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${getColorClasses(metrica.color)}`}>
                <metrica.icon className="h-6 w-6" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metrica.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrica.valor}</p>
                <div className="flex items-center mt-1">
                  <span className={`text-sm ${metrica.cambio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {metrica.cambio >= 0 ? '+' : ''}{metrica.cambio}%
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">vs mes anterior</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabla de rendimiento por vehículo */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Rendimiento por Vehículo
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            Detalle del rendimiento individual de cada vehículo en el período seleccionado
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Vehículo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Kilómetros
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Viajes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Eficiencia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Tiempo Promedio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Ingresos
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {vehiculosFiltrados.map((vehiculo) => (
                <tr key={vehiculo.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <TruckIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {vehiculo.placa}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {vehiculo.modelo}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {vehiculo.kmRecorridos.toLocaleString()} km
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {vehiculo.horasOperacion}h operación
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {vehiculo.viajesCompletados} viajes
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {(vehiculo.kmRecorridos / vehiculo.viajesCompletados).toFixed(0)} km/viaje
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {vehiculo.eficienciaCombustible} km/L
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {vehiculo.consumoCombustible}L consumidos
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900 dark:text-white">
                      <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {vehiculo.tiempoPromedioPorViaje}h
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      ${vehiculo.ingresos.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      ${(vehiculo.ingresos / vehiculo.viajesCompletados).toLocaleString()}/viaje
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {vehiculosFiltrados.length === 0 && (
          <div className="text-center py-8">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No hay datos</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No se encontraron datos de rendimiento para los filtros seleccionados.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaginaReporteRendimiento